import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { FreeMode, Pagination, Scrollbar } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import { useGetDealsQuery, useGetLoyaltyGroupsQuery, WeeklySchedule, DaySchedule } from '../../api';

import 'swiper/css/bundle';
import './deals-modal.sass';
import './loyalty/loyalty-details.sass';

export default function DealsModal() {
  const navigate = useNavigate();
  const [selectedGroupId, setSelectedGroupId] = useState<number | undefined>(undefined);
  const [showGroupSelection, setShowGroupSelection] = useState(true);

  const { data: deals, isLoading, error } = useGetDealsQuery({ loyaltyGroupId: selectedGroupId });
  const { data: loyaltyGroups = [], isLoading: isLoadingGroups } = useGetLoyaltyGroupsQuery();

  const handleGroupSelect = (groupId: number | undefined) => {
    setSelectedGroupId(groupId);
    setShowGroupSelection(false);
  };

  const formatTime = (time: string): string => {
    if (!time) return '';
    // Use 24-hour format
    return time;
  };

  const formatDaySchedule = (daySchedule: DaySchedule): JSX.Element => {
    if (!daySchedule.isAvailable) return <span>Unavailable</span>;

    // Check if it's all day (8am to 8am next day)
    if (daySchedule.startTime === '08:00' && daySchedule.endTime === '08:00') return <span>All Day</span>;

    const startTime = formatTime(daySchedule.startTime);
    const endTime = formatTime(daySchedule.endTime);

    return (
      <span>
        {startTime}
        <br />
        {endTime}
      </span>
    );
  };

  const getDayName = (day: keyof WeeklySchedule): string => {
    const dayNames = {
      monday: 'Mon',
      tuesday: 'Tue',
      wednesday: 'Wed',
      thursday: 'Thu',
      friday: 'Fri',
      saturday: 'Sat',
      sunday: 'Sun',
    };
    return dayNames[day];
  };

  const days: (keyof WeeklySchedule)[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

  return (
    <div className="pos-modal-portal">
      <div
        className="pos-modal-overlay pos-modal-overlay--fullscreen"
        onClick={(e) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          if ((e.target as any).className.includes('pos-modal-overlay')) navigate('/order');
        }}
      >
        <div className="pos-modal pos-modal--fullscreen">
          <div className="pos-modal__content pos-modal__content--fullscreen">
            <div className="deals-modal">
              <div className="deals-modal__header">
                <h2 className="deals-modal__title">{showGroupSelection ? 'Select Loyalty Group' : 'Available Deals - Weekly Schedule'}</h2>
                <button className="deals-modal__close" onClick={() => navigate('/order')} aria-label="Close">
                  ×
                </button>
              </div>

              {showGroupSelection ? (
                <div className="deals-modal__group-selection">
                  {isLoadingGroups ? (
                    <div className="deals-modal__loading">Loading groups...</div>
                  ) : (
                    <div className="deals-modal__group-list">
                      <Swiper
                        freeMode={true}
                        slidesPerView="auto"
                        spaceBetween={15}
                        mousewheel={true}
                        direction="vertical"
                        scrollbar={{ draggable: true }}
                        modules={[FreeMode, Scrollbar]}
                        className="deals-modal__group-swiper-vertical"
                      >
                        <SwiperSlide className="deals-modal__group-slide-vertical">
                          <button className="deals-modal__group-button-large" onClick={() => handleGroupSelect(undefined)}>
                            <div className="deals-modal__group-button-content">
                              <h3>Default Deals - No Loyalty Scanned</h3>
                              <p>View deals available to everyone</p>
                            </div>
                          </button>
                        </SwiperSlide>
                        {loyaltyGroups.map((group) => (
                          <SwiperSlide key={group.id} className="deals-modal__group-slide-vertical">
                            <button className="deals-modal__group-button-large" onClick={() => handleGroupSelect(group.id)}>
                              <div className="deals-modal__group-button-content">
                                <h3>{group.name}</h3>
                                <p>View deals for this group</p>
                              </div>
                            </button>
                          </SwiperSlide>
                        ))}
                      </Swiper>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <div className="deals-modal__selected-group">
                    <span className="deals-modal__selected-group-label">Selected Group:</span>
                    <span className="deals-modal__selected-group-name">
                      {selectedGroupId ? loyaltyGroups.find((g) => g.id === selectedGroupId)?.name || 'Unknown' : 'No Loyalty Scanned'}
                    </span>
                    <button className="deals-modal__change-group" onClick={() => setShowGroupSelection(true)}>
                      Change
                    </button>
                  </div>
                </>
              )}

              {!showGroupSelection && isLoading && <div className="deals-modal__loading">Loading deals...</div>}

              {!showGroupSelection && error && <div className="deals-modal__error">Error loading deals. Please try again.</div>}

              {!showGroupSelection && deals && deals.length > 0 && (
                <div className="deals-modal__table-container">
                  <div className="deals-modal__table-info">
                    Showing {deals.length} deals{selectedGroupId ? ' for selected group' : ' (Not Requiring Loyalty Scanned)'}
                  </div>
                  <table className="deals-table">
                    <thead>
                      <tr>
                        <th className="deals-table__deal-name">Deal Name</th>
                        {days.map((day) => (
                          <th key={day} className="deals-table__day">
                            {getDayName(day)}
                          </th>
                        ))}
                      </tr>
                    </thead>
                  </table>
                  <div className="deals-table__scroll-container">
                    <Swiper
                      freeMode={true}
                      height={400}
                      slidesPerView="auto"
                      spaceBetween={0}
                      mousewheel={true}
                      scrollbar={true}
                      direction="vertical"
                      pagination={{ clickable: true }}
                      modules={[FreeMode, Pagination]}
                    >
                      <SwiperSlide>
                        <table className="deals-table deals-table--body">
                          <tbody>
                            {deals.map((deal) => (
                              <tr key={deal.dealId} className={!deal.isDealAvailableNow ? 'deals-table__row--inactive' : ''}>
                                <td className="deals-table__deal-name">
                                  <div className="deals-table__deal-info">
                                    <span className="deals-table__deal-title">{deal.dealName}</span>
                                    {deal.hasUsageLimit && <span className="deals-table__deal-limit">Limit: {deal.usageLimit} uses</span>}
                                  </div>
                                </td>
                                {days.map((day) => (
                                  <td key={day} className="deals-table__schedule-cell">
                                    <span
                                      className={`deals-table__schedule ${
                                        !deal.weeklySchedule[day].isAvailable ? 'deals-table__schedule--closed' : ''
                                      }`}
                                    >
                                      {formatDaySchedule(deal.weeklySchedule[day])}
                                    </span>
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </SwiperSlide>
                    </Swiper>
                  </div>
                </div>
              )}

              {!showGroupSelection && deals && deals.length === 0 && !isLoading && (
                <div className="deals-modal__no-deals">
                  {selectedGroupId ? 'No deals available for the selected group.' : 'No deals available.'}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
